import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, Trophy, Users, Save, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";

export default function VendorLoyalty() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const shopId = useMemo(() => (user?.user_id ? `shop_${user.user_id}` : null), [user]);
  const [program, setProgram] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get(`/shops/${shopId}/loyalty`),
        api.get(`/shops/${shopId}/loyalty/customers`).catch(() => ({ data: { items: [] } })),
      ]);
      setProgram(pRes.data.program);
      setCustomers(cRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load loyalty program");
    } finally { setLoading(false); }
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  if (user && !user.is_vendor) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-3" data-testid="vendor-loyalty-not-vendor">
        <Lock className="w-10 h-10 text-zinc-500 mx-auto" />
        <h1 className="text-3xl font-black tracking-tighter">Vendors only</h1>
        <Button onClick={() => navigate("/sell")} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-11 px-5">Open Sell</Button>
      </div>
    );
  }

  const save = async () => {
    if (!program) return;
    setSaving(true);
    try {
      const payload = {
        is_active: !!program.is_active,
        name: program.name,
        points_per_dollar: Math.max(0, Math.min(200, Number(program.points_per_dollar) || 0)),
        redeem_rate_cents_per_point: Math.max(1, Math.min(100, Number(program.redeem_rate_cents_per_point) || 1)),
        min_redeem_points: Math.max(0, Number(program.min_redeem_points) || 0),
        max_redeem_pct: Math.max(1, Math.min(100, Number(program.max_redeem_pct) || 50)),
        tiers: program.tiers || [],
      };
      await api.put(`/shops/${shopId}/loyalty`, payload);
      toast.success("Loyalty program updated.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const updateTier = (idx, patch) => {
    setProgram((p) => {
      const tiers = [...(p.tiers || [])];
      tiers[idx] = { ...tiers[idx], ...patch };
      return { ...p, tiers };
    });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="vendor-loyalty-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="vendor-loyalty-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-zinc-950 to-pink-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Trophy className="w-3 h-3" /> Vendor Marketing
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Loyalty rewards</h1>
        <p className="text-sm text-zinc-400 mt-1">Give returning customers points at your shop. Points are vendor-specific — they can only be redeemed with you.</p>
      </header>

      {loading || !program ? (
        <div className="py-6 text-center text-zinc-500 text-sm inline-flex items-center gap-2 justify-center w-full"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5 space-y-3" data-testid="vendor-loyalty-settings">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Program active</Label>
              <Switch checked={!!program.is_active} onCheckedChange={(v) => setProgram({ ...program, is_active: v })} data-testid="vendor-loyalty-active" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Program name</Label>
              <Input value={program.name || ""} onChange={(e) => setProgram({ ...program, name: e.target.value })} maxLength={80} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="vendor-loyalty-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Points per $1 earned</Label>
                <Input type="number" min="0" max="200" value={program.points_per_dollar} onChange={(e) => setProgram({ ...program, points_per_dollar: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="vendor-loyalty-points-per-dollar" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Cents per point when redeemed</Label>
                <Input type="number" min="1" max="100" value={program.redeem_rate_cents_per_point} onChange={(e) => setProgram({ ...program, redeem_rate_cents_per_point: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="vendor-loyalty-cents-per-point" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Minimum points to redeem</Label>
                <Input type="number" min="0" value={program.min_redeem_points} onChange={(e) => setProgram({ ...program, min_redeem_points: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="vendor-loyalty-min-redeem" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Max % of subtotal</Label>
                <Input type="number" min="1" max="100" value={program.max_redeem_pct} onChange={(e) => setProgram({ ...program, max_redeem_pct: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="vendor-loyalty-max-pct" />
              </div>
            </div>
            <div>
              <p className="text-zinc-300 text-xs uppercase tracking-widest font-bold mb-2">Tiers</p>
              <div className="space-y-2">
                {(program.tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-6 gap-2" data-testid={`vendor-loyalty-tier-${i}`}>
                    <Input type="number" value={t.threshold || 0} onChange={(e) => updateTier(i, { threshold: Number(e.target.value) || 0 })} className="h-10 bg-white/5 border-white/10 rounded-xl text-white text-xs col-span-1" placeholder="Threshold" />
                    <Input value={t.label || ""} onChange={(e) => updateTier(i, { label: e.target.value })} className="h-10 bg-white/5 border-white/10 rounded-xl text-white text-xs col-span-1" placeholder="Label" />
                    <Textarea value={t.perk || ""} onChange={(e) => updateTier(i, { perk: e.target.value })} className="min-h-10 bg-white/5 border-white/10 rounded-xl text-white text-xs col-span-4" placeholder="Perk" />
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="w-full h-12 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold" data-testid="vendor-loyalty-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save program
            </Button>
          </section>

          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="vendor-loyalty-customers">
            <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2"><Users className="w-4 h-4" /> Top loyal customers ({customers.length})</h2>
            {customers.length === 0 ? (
              <p className="text-sm text-zinc-500 mt-2">No customers have earned points yet.</p>
            ) : (
              <div className="mt-3 space-y-1">
                {customers.slice(0, 20).map((c) => (
                  <div key={c.user_id} className="flex items-center justify-between text-sm py-2 border-b border-white/5">
                    <div className="min-w-0">
                      <p className="font-bold truncate">{c.user?.name || c.user_id}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{c.user?.email}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-black text-amber-300">{c.points?.toLocaleString()}</p>
                      <p className="text-[10px] text-zinc-500">lifetime {c.lifetime_points?.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
